"use client";

/**
 * AlertCanvas — icy silver-blue React Flow canvas for the artifact panel when
 * session.view_mode === "alert".
 *
 * Fixes in this pass:
 *  - Kills the infinite render loop: node/edge arrays are now gated by a
 *    stable `signature` string derived from the SWR payload, so SWR polling
 *    does not thrash React Flow state when nothing has changed.
 *  - Passes raw cell.outputs straight through (no normalization) so the modal
 *    Output tab renders with the same logic NotebookCell uses.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Background,
  BackgroundVariant,
  type Node,
  type Edge,
  type NodeTypes,
  type EdgeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { motion, AnimatePresence } from "framer-motion";
import { usePathname, useSearchParams } from "next/navigation";
import useSWR from "swr";
import {
  Bell,
  Sparkles,
  Clock,
  Inbox,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-is-mobile";

import { AlertCellNode, type AlertCellNodeData } from "./AlertCellNode";
import { AlertCreatorNode, type AlertCreatorNodeData } from "./AlertCreatorNode";
import { AlertFlowingEdge } from "./AlertFlowingEdge";
import {
  AlertCellModalProvider,
} from "./AlertCellModalContext";
import { AlertCellModal } from "./AlertCellModal";
import { API_BASE_URL } from "@/lib/config";
import { TITLE_CLASS } from "@/lib/alert-theme";
import { apiClient, type AlertSnapshot, type PublicProfile } from "@/lib/api-client";
import { useAlertPublicView } from "@/components/alert-page/AlertPaywallContext";
import { alertLog, summarizeOutput } from "@/lib/alert-display-log";
import {
  isDashboardOutput,
  isNoiseStdoutText,
} from "@/lib/alert-output-detection";
import { createChannelLogger } from "@/lib/debug-log";
import { telemetry } from "@/lib/telemetry";
import {
  AUTOMATIONS_ALERT_TOUR_QUERY_PARAM,
  AUTOMATIONS_ALERT_TOUR_QUERY_VALUE,
} from "@/lib/onboarding-constants";

const timingLog = createChannelLogger("alert_timing");
function tSinceNav(): number {
  if (typeof performance === "undefined") return -1;
  const navs = performance.getEntriesByType?.("navigation") as PerformanceNavigationTiming[] | undefined;
  const navStart = navs?.[0]?.startTime ?? 0;
  return Math.round(performance.now() - navStart);
}

// Two-column zig-zag grid spacing. Nodes are 380px wide.
// Keep this in sync with the default card max height in AlertCellNode.
const NODE_WIDTH = 380;
const COL_GAP = 110;
const NODE_ROW_HEIGHT = 340;
const ROW_GAP = 100;
const COL_X_LEFT = 0;
const COL_X_RIGHT = NODE_WIDTH + COL_GAP;
const ROW_STEP = NODE_ROW_HEIGHT + ROW_GAP;

// On the /alerts/[id] page, the LAST node gets a special treatment: when
// it has a dashboard output it is hoisted into its OWN right-hand column,
// widened, and made tall enough to clearly show the dashboard preview.
// The column sits to the right of the zig-zag and the last edge re-routes
// into it. Dimensions chosen so the dashboard preview actually breathes —
// the user explicitly asked for a big, clearly dominant hero with NO
// empty cyan space around the preview.
const HERO_NODE_WIDTH = 720;
const HERO_NODE_HEIGHT = 600;
const HERO_COL_GAP = 140;
const HERO_COL_X = COL_X_RIGHT + NODE_WIDTH + HERO_COL_GAP;

const nodeTypes: NodeTypes = {
  alertCell: AlertCellNode,
  // Floating "Made by [creator]" card — disconnected from the alert flow.
  // Only injected when `presentedBy.kind === "creator"`; anonymous and
  // missing bylines skip the node entirely so non-curated alerts stay
  // visually identical to how they've always rendered.
  creatorCard: AlertCreatorNode,
};
const edgeTypes: EdgeTypes = { alertEdge: AlertFlowingEdge };

// Creator node geometry. Approximate (no measurement) — only used to
// center-align the card above the cell grid. Slightly undersells the real
// height so fitView still leaves breathing room around it.
const CREATOR_NODE_WIDTH = 520;
const CREATOR_NODE_HEIGHT = 180;
// Horizontal center of the FULL canvas footprint, not just the zig-zag:
//   - Zig-zag cells span  [0, COL_X_RIGHT + NODE_WIDTH]  → [0, 870]
//   - Hero column (when active on /alerts/[id]) spans    [HERO_COL_X, HERO_COL_X + HERO_NODE_WIDTH] → [1010, 1730]
// The /alerts/[id] route almost always renders the hero, so centering
// against the zig-zag alone leaves the card visually stranded far to
// the left. Center against the hero-inclusive span so the card lands
// above the middle of what the user actually sees.
const CREATOR_NODE_CANVAS_LEFT = COL_X_LEFT;
const CREATOR_NODE_CANVAS_RIGHT = HERO_COL_X + HERO_NODE_WIDTH;
const CREATOR_NODE_X =
  (CREATOR_NODE_CANVAS_LEFT + CREATOR_NODE_CANVAS_RIGHT) / 2 - CREATOR_NODE_WIDTH / 2;
// Hoist it above row 0 with the same gap the columns use between themselves
// so the card reads as a deliberate companion, not a stray artifact.
const CREATOR_NODE_Y = -(CREATOR_NODE_HEIGHT + COL_GAP);

interface RawCell {
  id?: string;
  index?: number;
  purpose?: string;
  content?: string;
  source?: string;
  status?: string;
  outputs?: Array<any>;
}

interface ActiveRunResponse {
  id?: string;
  cells?: RawCell[];
  status?: string;
  updatedAt?: string;
}

const fetcher = async (url: string) => {
  const startT = tSinceNav();
  timingLog("fetch:active_run:start", { t: startT, url });
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) {
    timingLog("fetch:active_run:error", {
      t: tSinceNav(),
      duration_ms: tSinceNav() - startT,
      status: r.status,
    });
    throw new Error(`runs fetch ${r.status}`);
  }
  const result = (await r.json()) as ActiveRunResponse;
  timingLog("fetch:active_run:done", {
    t: tSinceNav(),
    duration_ms: tSinceNav() - startT,
    cellCount: result?.cells?.length ?? 0,
  });
  return result;
};

function mapStatus(raw?: string): AlertCellNodeData["status"] {
  switch ((raw || "").toLowerCase()) {
    case "success":
      return "success";
    case "error":
      return "error";
    case "executing":
    case "running":
      return "running";
    case "pending":
      return "pending";
    default:
      return "idle";
  }
}

function extractStdout(cell: RawCell): string {
  if (!Array.isArray(cell.outputs)) return "";
  const parts: string[] = [];
  for (const out of cell.outputs) {
    if (!out) continue;
    if (out.type === "text" && typeof out.data === "string") {
      // Skip PKA's "[Plotly Figure]" placeholder so the fallback
      // ctx.stdout string never contains noise lines.
      if (!isNoiseStdoutText(out.data)) parts.push(out.data);
    } else if (out.output_type === "stream" && typeof out.text === "string") {
      if (!isNoiseStdoutText(out.text)) parts.push(out.text);
    } else if (
      out.data &&
      typeof out.data === "object" &&
      typeof out.data["text/plain"] === "string"
    ) {
      const tp = out.data["text/plain"];
      if (!isNoiseStdoutText(tp)) parts.push(tp);
    }
  }
  return parts.join("\n").trim();
}

/**
 * Build a stable signature string from cells. React's shallow compare of
 * arrays-from-SWR is unreliable (SWR hands us a fresh array every poll),
 * so we key our useMemos/useEffects off THIS string instead.
 */
function cellsSignature(cells: RawCell[]): string {
  return cells
    .map((c) => {
      const id = c.id ?? c.index ?? "?";
      const idx = c.index ?? -1;
      const status = (c.status || "").toLowerCase();
      const purpose = c.purpose || "";
      const src = c.content || c.source || "";
      const outLen = Array.isArray(c.outputs) ? c.outputs.length : 0;
      // Short hash of content + purpose length so we detect edits.
      return `${id}|${idx}|${status}|${purpose.length}|${src.length}|${outLen}`;
    })
    .join(";");
}

function AlertCanvasInner({
  sessionId,
  alertStartCellIndex,
  isStreaming,
  isAlertsRoute,
  cellDescriptions,
  alertId,
  isOwner,
  isSubscribed,
  presentedBy,
  viewMode,
}: {
  sessionId: string;
  alertStartCellIndex: number | null;
  isStreaming: boolean;
  isAlertsRoute: boolean;
  cellDescriptions: Record<string, string> | null;
  alertId: string | undefined;
  isOwner: boolean;
  isSubscribed: boolean;
  /** Public byline from `GET /alerts/{id}`. Only `kind === "creator"` payloads
   *  produce a canvas node; anonymous / null skip the byline entirely. */
  presentedBy: PublicProfile | null | undefined;
  viewMode?: string;
}) {
  // Log AlertCanvas first call (this is the first measurable render of the
  // canvas component — fires AFTER Chat+Artifact mount).
  const _canvasMountLogged = useRef(false);
  if (!_canvasMountLogged.current) {
    _canvasMountLogged.current = true;
    timingLog("canvas:first_call", { t: tSinceNav(), sessionId });
  }

  // Local "dramatic mode" — kicked on when a Run Now just happened. Uses a
  // ref+state to bump SWR poll rate to 1.5s for ~20s after we see the first
  // cell go from idle → running, then gracefully falls back to idle polling.
  const [dramatic, setDramatic] = useState(false);

  // Hoisted to the top so `derivedNodes` (mobile single-column layout) and
  // the fitView effect below can both read it. Previously this hook was
  // declared alongside the useReactFlow helpers lower down, but TS then
  // flagged derivedNodes' use of `isMobile` as "used before declaration".
  const isMobile = useIsMobile();

  // Gate the visibility of the whole flow on fitView having completed. This
  // prevents the ugly "nodes appear off-screen left, then re-fit" flash we
  // saw on first mount.
  const [viewportReady, setViewportReady] = useState(false);

  // Reset mount gate ONLY when the user genuinely navigates to a different
  // alert session — NOT on the first render, and NOT on every re-render
  // that happens to run this effect while `sessionId` is still the same.
  //
  // Without this guard, any transient parent re-render (e.g. isStreaming
  // flipping from false → true on first user message) combined with a
  // bundler/HMR quirk can cause the whole ReactFlow to fade back in
  // because viewportReady was flipped to `false` and the
  // `.alert-canvas-mounting` → `.alert-canvas-ready` CSS transition
  // re-ran on the existing node DOM. Tracking the previously seen session
  // in a ref makes this effect a true "session change" signal.
  const lastSeenSessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastSeenSessionIdRef.current === null) {
      lastSeenSessionIdRef.current = sessionId;
      return;
    }
    if (lastSeenSessionIdRef.current !== sessionId) {
      lastSeenSessionIdRef.current = sessionId;
      setViewportReady(false);
    }
  }, [sessionId]);

  // On a /replay/* route the chat session is ephemeral (an in-memory UUID
  // with no DB record), so the live `/strategies/active-run-details` lookup
  // 404s. The replay backend exposes a parallel `/replay/notebook/{id}`
  // that returns the same shape from `_replay_cells` accumulated as
  // add_cell tool calls stream during the replay. Without this branch,
  // when switch_mode flips viewMode to "alert" mid-replay the canvas
  // mounts but renders empty, defeating the live build-out.
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isPublicSignalTour =
    searchParams?.get(AUTOMATIONS_ALERT_TOUR_QUERY_PARAM) ===
    AUTOMATIONS_ALERT_TOUR_QUERY_VALUE;
  const isReplay = Boolean(pathname?.startsWith("/replay/"));
  const cellsUrl = sessionId
    ? isReplay
      ? `${API_BASE_URL}/replay/notebook/${sessionId}`
      : `${API_BASE_URL}/strategies/active-run-details/${sessionId}`
    : null;
  const { data, error: swrError, isLoading: cellsLoading } = useSWR<ActiveRunResponse>(
    cellsUrl,
    fetcher,
    {
      // Poll fast while streaming (alert just uploaded) OR dramatic (alert
      // mid-run). Otherwise relax to 6s.
      refreshInterval: isStreaming || dramatic ? 1500 : 6000,
      revalidateOnFocus: true,
      dedupingInterval: 500,
      // Keep the previous response visible while SWR is refetching after a
      // config change (e.g. refreshInterval flipped because `isStreaming`
      // went true on the first user message). Without this, `data` can
      // briefly become `undefined`, which makes `alertCells` empty,
      // `empty` true, and the AnimatePresence swaps from "flow" to
      // "empty" and back — the canvas "reload" flicker the user sees.
      keepPreviousData: true,
    },
  );

  // ─── Dashboard snapshot (Loops 3+4 + paid-tier gating rewrite) ────────
  // Every run saves a dashboard-HTML snapshot. Which one we show on the
  // "Final Results" node is controlled by the public-view toggle state
  // lifted to a page-level context so the header + canvas share one
  // source of truth:
  //   - Owner                    → always LIVE (latest snapshot)
  //   - Paid viewer, view=live   → /snapshots/latest
  //   - Paid viewer, view=delayed→ /snapshots/first  (preview the stale
  //                                teaser — useful for QA)
  //   - Free viewer              → /snapshots/first  (context prevents
  //                                them from flipping without a paywall)
  //   - /chat alert-mode         → no provider, hook returns defaults;
  //                                alertId is undefined so no fetch
  //
  // Polling: only LIVE polls (30s). DELAYED is immutable (first-ever
  // snapshot), so no refresh needed.
  const publicView = useAlertPublicView();
  const effectiveSnapshotMode: "latest" | "first" =
    isOwner
      ? "latest"
      : publicView.view === "live" && publicView.canGoLive
        ? "latest"
        : "first";
  const snapshotSwrKey = alertId
    ? `alert-snap-${alertId}-${effectiveSnapshotMode}`
    : null;
  const { data: snapshotData } = useSWR<{ snapshot: AlertSnapshot } | null>(
    snapshotSwrKey,
    async () => {
      if (!alertId) return null;
      const fetchOnce = () =>
        effectiveSnapshotMode === "latest"
          ? apiClient.getAlertSnapshotLatest(alertId)
          : apiClient.getAlertSnapshotFirst(alertId);
      // Diagnostic probe (additive, no behavior change): the snapshot fetch
      // is the ONE hop with no observability — its result silently becomes
      // `null` on any error, which then strips the hero dashboard to a blank
      // card (the "dashboard not showing" bug). We emit the real outcome
      // (status / html length / error) so we can see, per page load, WHY a
      // given owner/viewer got null instead of the fresh snapshot.
      const emitOk = (res: { snapshot?: AlertSnapshot } | null, attempt: number) => {
        try {
          telemetry.emit("artifact.snapshot_fetch_ok", {
            session_id: sessionId,
            alert_id: alertId,
            mode: effectiveSnapshotMode,
            is_owner: isOwner,
            is_subscribed: isSubscribed,
            attempt,
            has_snapshot: Boolean(res?.snapshot),
            html_len: res?.snapshot?.html ? String(res.snapshot.html).length : 0,
            has_image: Boolean(res?.snapshot?.image_url),
            run_status: res?.snapshot?.run_status ?? null,
          });
        } catch {}
      };
      const emitFail = (err: unknown, attempt: number, gaveUp: boolean) => {
        try {
          const anyErr = err as { status?: number; message?: string } | undefined;
          telemetry.emit("artifact.snapshot_fetch_failed", {
            session_id: sessionId,
            alert_id: alertId,
            mode: effectiveSnapshotMode,
            is_owner: isOwner,
            is_subscribed: isSubscribed,
            attempt,
            gave_up: gaveUp,
            error_status: anyErr?.status ?? null,
            error_message: (anyErr?.message || String(err)).slice(0, 300),
          });
        } catch {}
      };
      try {
        const res = await fetchOnce();
        emitOk(res, 1);
        return res;
      } catch (err) {
        // 403 (free user tried /latest) / 404 (no snapshot yet) are both
        // non-fatal. But for the LIVE/owner view a transient failure here
        // used to silently drop us to the cell's own (possibly STALE draft)
        // outputs — the "last ran Xm ago but results are old" split-brain.
        // So for `latest` we retry ONCE before giving up; that keeps the
        // owner on the real freshest snapshot through a blip instead of
        // showing stale dashboard HTML as if it were current.
        if (effectiveSnapshotMode === "latest") {
          emitFail(err, 1, false);
          try {
            const res2 = await fetchOnce();
            emitOk(res2, 2);
            return res2;
          } catch (err2) {
            emitFail(err2, 2, true);
            return null;
          }
        }
        emitFail(err, 1, true);
        return null;
      }
    },
    {
      refreshInterval: effectiveSnapshotMode === "latest" ? 30_000 : 0,
      revalidateOnFocus: false,
      dedupingInterval: 5_000,
      shouldRetryOnError: false,
    },
  );
  const snapshotHtml = snapshotData?.snapshot?.html ?? null;
  // When the PKA image-gen pipeline succeeded for this run the backend
  // returns `image_url` alongside the HTML. We prefer the image when
  // available — the hero node substitutes a `dashboard_image` output
  // for the legacy `html` one so <DashboardImage> wins over the iframe.
  const snapshotImageUrl = snapshotData?.snapshot?.image_url ?? null;
  const snapshotImageGeneratedAt =
    snapshotData?.snapshot?.image_generated_at ?? null;
  const snapshotImageModel = snapshotData?.snapshot?.image_model ?? null;
  const snapshotFingerprint = useMemo(
    () =>
      [
        effectiveSnapshotMode,
        snapshotData?.snapshot?.id ?? "none",
        snapshotHtml ? String(snapshotHtml).length : 0,
        snapshotImageUrl ?? "",
      ].join(":"),
    [effectiveSnapshotMode, snapshotData?.snapshot?.id, snapshotHtml, snapshotImageUrl],
  );

  // Flip `dramatic` on/off based on cell statuses. Anything running = hot.
  useEffect(() => {
    const cells = data?.cells || [];
    const anyRunning = cells.some(
      (c) =>
        (c.status || "").toLowerCase() === "running" ||
        (c.status || "").toLowerCase() === "executing",
    );
    if (anyRunning && !dramatic) setDramatic(true);
    if (!anyRunning && dramatic) {
      // cooldown — wait 2s after last running cell disappears so the user
      // sees the final success state refresh at the fast rate.
      const t = window.setTimeout(() => setDramatic(false), 2000);
      return () => window.clearTimeout(t);
    }
  }, [data, dramatic]);

  // Diagnostic: surface what active-run-details returned so we can tell
  // "canvas empty because no sessionId" from "canvas empty because the
  // session has no cells" from "canvas empty because fetch errored".
  useEffect(() => {
    if (swrError) {
      // eslint-disable-next-line no-console
      console.error("[AlertCanvas] active-run-details fetch error:", swrError);
      alertLog("canvas:fetch_error", {
        sessionId,
        error:
          swrError instanceof Error ? swrError.message : String(swrError),
      });
      telemetry.emit("artifact.canvas_fetch_error", {
        session_id: sessionId,
        alert_id: alertId,
        is_owner: isOwner,
        is_subscribed: isSubscribed,
        is_alerts_route: isAlertsRoute,
        error_message:
          swrError instanceof Error ? swrError.message.slice(0, 500) : String(swrError).slice(0, 500),
      });
      return;
    }
    if (data) {
      const cells = Array.isArray(data.cells) ? data.cells : [];
      timingLog("canvas:data_received", {
        t: tSinceNav(),
        cell_count: cells.length,
      });
      alertLog("canvas:fetch_ok", {
        sessionId,
        alertStartCellIndex,
        cell_count: cells.length,
        statuses: cells.map((c) => c.status),
        indices: cells.map((c) => c.index),
        // Per-cell output summary so we can see e.g. cell 5 has [html, plotly, dashboard_html]
        // without dumping 50kb of HTML into the log.
        outputs_by_cell: cells.map((c) => ({
          index: c.index,
          status: c.status,
          outputs: (Array.isArray(c.outputs) ? c.outputs : []).map(
            summarizeOutput,
          ),
        })),
      });
      telemetry.emit("artifact.canvas_fetch_ok", {
        session_id: sessionId,
        alert_id: alertId,
        is_owner: isOwner,
        is_subscribed: isSubscribed,
        is_alerts_route: isAlertsRoute,
        alert_start_cell_index: alertStartCellIndex,
        cell_count: cells.length,
        statuses: cells.map((c) => c.status).slice(0, 30),
        indices: cells.map((c) => c.index).slice(0, 30),
      });
    }
  }, [alertId, alertStartCellIndex, data, isAlertsRoute, isOwner, isSubscribed, sessionId, swrError]);

  // Filter to alert-scope cells, then build a stable signature from them.
  // We intentionally depend on the SIGNATURE in downstream hooks, not the
  // array reference — that's the infinite-loop fix.
  const alertCells = useMemo<RawCell[]>(() => {
    const all = data?.cells ?? [];
    if (alertStartCellIndex == null) return [];
    return all
      .filter((c) => typeof c.index === "number" && c.index! >= alertStartCellIndex)
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  }, [data, alertStartCellIndex]);

  const signature = useMemo(
    () => cellsSignature(alertCells),
    [alertCells],
  );

  useEffect(() => {
    alertLog("canvas:alert_cells_filtered", {
      sessionId,
      alertStartCellIndex,
      total_cells_from_api: Array.isArray(data?.cells) ? data!.cells!.length : 0,
      filtered_alert_cells: alertCells.length,
      indices: alertCells.map((c) => c.index),
      statuses: alertCells.map((c) => c.status),
      signature,
    });
    // Mirror the critical diagnostic fields into the alert_timing channel
    // so a single log file carries the full mobile-landing trace (page
    // render → chat branch → artifact render → canvas cells). If this log
    // shows filtered=0 while total>0, alertStartCellIndex is wrong. If
    // it shows filtered>0 but nodes still don't render, the bug is
    // downstream in derivedNodes / viewportReady / motion.div gating.
    timingLog("canvas:cells", {
      t: tSinceNav(),
      total: Array.isArray(data?.cells) ? data!.cells!.length : 0,
      filtered: alertCells.length,
      alertStartCellIndex,
    });
    telemetry.emit("artifact.canvas_cells", {
      session_id: sessionId,
      alert_id: alertId,
      alert_start_cell_index: alertStartCellIndex,
      total_cells_from_api: Array.isArray(data?.cells) ? data!.cells!.length : 0,
      filtered_alert_cells: alertCells.length,
      indices: alertCells.map((c) => c.index).slice(0, 30),
      statuses: alertCells.map((c) => c.status).slice(0, 30),
      signature,
    });
  }, [signature, alertCells, sessionId, alertStartCellIndex, data, alertId]);

  // Carousel-freeze signal: when the alert is mid-run (any cell executing OR
  // dramatic-mode still active during cooldown), we do NOT want the nodes
  // flipping between code/purpose/output views — the user is watching real
  // execution state and the flip would be disorienting. This is read by
  // `AlertCellNode`→`AlertCellCarousel` via the `carouselPaused` node-data
  // field below.
  const anyNodeRunning = useMemo(() => {
    const cells = data?.cells || [];
    return cells.some(
      (c) =>
        (c.status || "").toLowerCase() === "running" ||
        (c.status || "").toLowerCase() === "executing",
    );
  }, [data]);
  const freezeCarousels = anyNodeRunning || dramatic;

  // Derived nodes/edges — memoized off the stable signature, not the array ref.
  //
  // Ordering contract: ALL cell nodes come first (indices 0..n-1), then the
  // optional creator byline node is appended at the very end. This matters
  // for the edge loop below — we only draw `alertEdge`s between consecutive
  // CELL nodes, so keeping the creator last means we can cap the loop at
  // `alertCells.length` without introspection. If we ever add more floating
  // nodes, follow the same pattern: cells first, standalone nodes last.
  const derivedNodes: Node[] = useMemo(() => {
    const total = alertCells.length;
    // "Hero node" = the LAST cell, promoted to its own right-hand column on
    // the /alerts/[id] page and forced to dashboard-only.
    //
    // Two conditions independently trigger the hero treatment:
    //   1. The cell's live outputs already carry a dashboard (old path —
    //      kept so /chat in alert-mode still works).
    //   2. We have a snapshot-HTML overlay from Loop 3+4 (new path — the
    //      /alerts page uses this regardless of whether the notebook's
    //      last run finished or not).
    // Either way, isHeroNode forces the last node into "Final Results"
    // styling + substitutes its outputs with either the snapshot HTML or
    // the live dashboard, whichever is available.
    const lastIdx = total - 1;
    const lastCell = total > 0 ? alertCells[lastIdx] : null;
    const lastOutputs = Array.isArray(lastCell?.outputs) ? lastCell!.outputs : [];
    const lastHasDashboard = lastOutputs.some((o) => isDashboardOutput(o));
    const hasSnapshotOverlay = Boolean(snapshotHtml || snapshotImageUrl);
    // Signals have no Gemini dashboard snapshot — the register cell's
    // signal-registration card is the hero "Final Results" node instead.
    // Clicking it routes to the Dashboard tab (the signal performance view).
    const lastHasSignalRegister = lastOutputs.some(
      (o) => (o as { type?: string }).type === "signal_register",
    );
    const isSignalHeroEligible =
      isAlertsRoute && viewMode === "signal" && total > 0 && lastHasSignalRegister;
    const hasHeroNode =
      isAlertsRoute &&
      total > 0 &&
      (lastHasDashboard || hasSnapshotOverlay || isSignalHeroEligible);

    // Fake an output object that matches what `isDashboardOutput` detects.
    // We preserve the real cell outputs for owner (they might want to see
    // the raw chart / stdout from the last run too in the modal), but for
    // non-owners we substitute WHOLESALE so the DELAYED / LIVE boundary is
    // strictly enforced — a stranger never sees fresh chart data that
    // leaked through the outputs array even if the backend forgot to
    // filter it.
    //
    // PREFERENCE ORDER:
    //   1. dashboard_image — the new PNG from GPT-5.4 Image 2 (if present)
    //   2. html overlay    — legacy iframe'd Gemini-authored HTML
    // The image wins when the backend gives us BOTH because it's the
    // richer visual. Falling back to HTML is silent (no UI flicker).
    const snapshotOutput = snapshotImageUrl
      ? ({
          type: "dashboard_image",
          data: {
            url: snapshotImageUrl,
            generated_at: snapshotImageGeneratedAt,
            model: snapshotImageModel,
          },
        } as any)
      : snapshotHtml
      ? ({ type: "dashboard", data: snapshotHtml } as any)
      : null;

    const cellNodes: Node[] = alertCells.map((c, i) => {
      const src = c.content || c.source || "";
      const hasAlertSend = /\balert\s*\.\s*send\s*\(/.test(src);
      const outputs = Array.isArray(c.outputs) ? c.outputs : [];
      const isHeroNode = hasHeroNode && i === lastIdx;

      // Override the hero node's outputs with the snapshot overlay. The
      // rule depends on the *view mode*, not just ownership:
      //   - DELAYED (effectiveSnapshotMode === "first"): show ONLY the
      //     stale snapshot. Strip any dashboard that might have leaked
      //     through the cell's live outputs so the "DELAYED" label
      //     stays honest. This applies to both free users (forced into
      //     delayed) and paid users who flipped to delayed for preview.
      //   - LIVE: show snapshot + cell's non-dashboard outputs (stdout /
      //     side effects). The snapshot IS the fresh dashboard, and
      //     anything else helps the viewer debug.
      const isSignalHero = isSignalHeroEligible && i === lastIdx;
      const heroOutputs = (() => {
        if (!isHeroNode) return outputs;
        // Signal hero: the register card IS the hero. Keep the cell's own
        // outputs (which carry the signal_register card) — never substitute a
        // Gemini dashboard snapshot.
        if (isSignalHero) return outputs;
        if (!snapshotOutput) {
          // No fresh snapshot loaded. In LIVE/owner mode we must NOT present
          // the cell's own dashboard output as the latest result — that data
          // comes from the linked session, which can point at a stale draft
          // notebook (the split-brain Mustafa reported). Strip dashboard
          // outputs so we never show old dashboard HTML as if it were fresh;
          // non-dashboard outputs (stdout/charts) are still safe to show.
          return outputs.filter((o) => !isDashboardOutput(o));
        }
        if (effectiveSnapshotMode === "first") {
          return [snapshotOutput];
        }
        return [snapshotOutput, ...outputs.filter((o) => !isDashboardOutput(o))];
      })();

      // Layout math:
      //   • Default (all nodes except hero) — two-column zig-zag:
      //       position 0 → (col 0, row 0)   — top-left
      //       position 1 → (col 1, row 0)   — top-right
      //       position 2 → (col 0, row 1)   — below 0 (zig-zag)
      //       position 3 → (col 1, row 1)
      //       ...
      //   • Hero (last node w/ dashboard on /alerts) — own RIGHT column,
      //     vertically centered against the zig-zag rows it replaces, made
      //     wider + taller so the dashboard preview reads clearly.
      let x: number;
      let y: number;
      if (isHeroNode) {
        // The hero stands in for the final zig-zag slot. We center it
        // vertically against the span of all OTHER nodes so edges from
        // the last zig-zag node curve neatly into it.
        const nonHeroCount = total - 1;
        const nonHeroLastRow = nonHeroCount > 0 ? Math.floor((nonHeroCount - 1) / 2) : 0;
        const zigzagHeight = nonHeroLastRow * ROW_STEP + NODE_ROW_HEIGHT;
        x = HERO_COL_X;
        y = Math.max(0, (zigzagHeight - HERO_NODE_HEIGHT) / 2);
      } else {
        const col = i % 2;
        const row = Math.floor(i / 2);
        x = col === 0 ? COL_X_LEFT : COL_X_RIGHT;
        y = row * ROW_STEP;
      }

      // Pre-generated "What it does / produces" markdown from the backend.
      // Stored on the alert doc under `cell_descriptions` keyed by FORK
      // cell index (as string). The linked session's notebook_id IS the
      // fork, so `c.index` is the fork index — direct lookup.
      // Cell 0 (setup) never has a description; older alerts may not
      // have them yet (lazy backfill runs in the background on first
      // owner fetch, appearing on the next page load).
      const descriptionMd =
        (cellDescriptions && c.index != null
          ? cellDescriptions[String(c.index)] ?? null
          : null);

      // Hero nodes are rebranded as "Final Results" — the cell's own
      // purpose string (typically "Alert 2 send — swing signal email
      // + dashboard") is too verbose for a hero slot and differs alert
      // to alert. We append the view mode in parens so toggling LIVE ↔
      // DELAYED actually produces visible delta (previously the only
      // change was the subline far away in the header, which was easy
      // to miss).
      const heroPurpose = isHeroNode
        ? isSignalHero
          ? "Final Results"
          : effectiveSnapshotMode === "latest"
            ? "Final Results"
            : "Final Results (Delayed Preview)"
        : c.purpose || "";

      const nodeData: AlertCellNodeData = {
        // DISPLAYED index is alert-relative (1-based), NOT notebook cell index.
        displayIndex: i + 1,
        // Keep the REAL cellIndex so the modal can call /alerts/explain-cell.
        cellIndex: (c.index ?? i) as number,
        purpose: heroPurpose,
        // On the hero node for NON-OWNERS we also hide the code panel —
        // it's the owner's strategy IP. Owners still get code in the
        // expanded modal because they're looking at their own thing.
        code: isHeroNode && !isOwner ? "" : src,
        codePreview: isHeroNode && !isOwner ? "" : src,
        stdout: isHeroNode && !isOwner ? "" : extractStdout(c),
        status: mapStatus(c.status),
        hasAlertSend,
        outputCount: heroOutputs.length,
        outputs: heroOutputs as unknown as AlertCellNodeData["outputs"],
        sessionId,
        carouselPaused: freezeCarousels,
        // NEW fields — drive carousel behavior + styling on the real /alerts
        // route vs /chat alert-mode sessions.
        isAlertsRoute,
        isHeroNode,
        // Signal hero routes to the Dashboard tab on click instead of opening
        // the cell modal (the register card is a preview of that dashboard).
        isSignalHero,
        variant: isHeroNode ? "hero" : "default",
        // Force the hero node to show ONLY the dashboard (no code/stdout
        // carousel tabs). Wired into AlertCellNode / AlertCellCarousel.
        forceDashboardOnly: isHeroNode,
        descriptionMd,
        // Viewer-state flags — consumed by AlertCellNode for the expand
        // paywall gate (Phase 6.1).
        viewerIsOwner: isOwner,
        viewerIsSubscribed: isSubscribed,
        viewMode,
      };
      return {
        id: `cell-${c.id || c.index}`,
        type: "alertCell",
        position: { x, y },
        data: nodeData,
        draggable: true,
        selectable: true,
        // CSS var used by the staggered reveal animation in AlertCanvas.
        style: { ["--alert-node-idx" as any]: String(i) } as React.CSSProperties,
      } as Node;
    });

    // Floating creator byline — only when the backend resolved a curated
    // creator. Anonymous + null payloads skip the node entirely so the
    // canvas looks identical to the legacy no-byline version. The node
    // sits ABOVE the zig-zag, horizontally centered on the cell grid,
    // with NO edges attaching it (React Flow ignores it for routing).
    // Appending it LAST preserves the "cells first" ordering the edge
    // loop depends on (see derivedEdges below).
    if (presentedBy && presentedBy.kind === "creator") {
      const creatorData: AlertCreatorNodeData = { profile: presentedBy };
      cellNodes.push({
        id: "creator-byline",
        type: "creatorCard",
        position: { x: CREATOR_NODE_X, y: CREATOR_NODE_Y },
        data: creatorData,
        draggable: true,
        selectable: false,
        // Reuse the same stagger CSS var; idx=-1 puts it slightly AHEAD
        // of the first cell in the fade-in timeline so the visitor reads
        // "Made by X" before the cells animate in.
        style: { ["--alert-node-idx" as any]: "-1" } as React.CSSProperties,
      } as Node);
    }

    return cellNodes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, sessionId, freezeCarousels, isAlertsRoute, cellDescriptions, snapshotHtml, snapshotImageUrl, snapshotImageGeneratedAt, snapshotImageModel, isOwner, isSubscribed, effectiveSnapshotMode, presentedBy]);

  const derivedEdges: Edge[] = useMemo(() => {
    const edges: Edge[] = [];
    // Bound by `alertCells.length`, NOT `derivedNodes.length` — the creator
    // byline node (when present) is always appended last, and connecting a
    // cell → creator with an `alertEdge` would put a stray arrow on the
    // canvas pointing at the author card. Standalone-by-design.
    for (let i = 1; i < alertCells.length; i++) {
      edges.push({
        id: `e-${i}`,
        source: derivedNodes[i - 1].id,
        target: derivedNodes[i].id,
        type: "alertEdge",
        // Mark new edges for the draw-in animation in AlertFlowingEdge.
        animated: false,
      });
    }
    return edges;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // React Flow-controlled state — seeded once, then synced via signature.
  const [nodes, setNodes, onNodesChange] = useNodesState(derivedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(derivedEdges);

  // Render log — captures the three things that actually determine whether
  // nodes visibly appear on mobile:
  //   1) derivedNodes.length — did we compute any at all?
  //   2) nodes.length — did ReactFlow's state accept them? (can lag behind
  //      derivedNodes if the sync effect hasn't run yet.)
  //   3) viewportReady — is the ReactFlow viewport gate open? If this
  //      stays false forever, nodes render but are hidden behind the
  //      opacity-0 wrapper.
  // If derivedNodes > 0 but nodes === 0 for multiple renders → sync bug.
  // If nodes > 0 but viewportReady stays false → fitView/onInit didn't run.
  timingLog("canvas:render", {
    t: tSinceNav(),
    isMobile,
    alertCellsCount: alertCells.length,
    derivedNodesCount: derivedNodes.length,
    nodesCount: nodes.length,
    viewportReady,
  });

  const prevSigRef = useRef<string>("");

  // Fingerprint of the cellDescriptions map so the sync effect re-runs when
  // descriptions arrive (e.g. from a legacy-alert lazy backfill landing
  // after initial mount). Without this, new descriptions would stay stuck
  // in `derivedNodes` but never reach the `nodes` state that ReactFlow
  // actually renders.
  const descriptionsFingerprint = useMemo(() => {
    if (!cellDescriptions) return "none";
    const keys = Object.keys(cellDescriptions).sort();
    // Use length of each value as a cheap hash — avoids stringifying huge
    // markdown blobs just to detect identity changes.
    return keys
      .map((k) => `${k}:${(cellDescriptions[k] || "").length}`)
      .join("|");
  }, [cellDescriptions]);

  // Single effect, gated by cells + descriptions + snapshot. Preserves
  // user-dragged positions while still refreshing the hero dashboard when
  // LIVE/DELAYED changes.
  useEffect(() => {
    const combined = `${signature}__${descriptionsFingerprint}__${snapshotFingerprint}`;
    if (prevSigRef.current === combined) return;
    prevSigRef.current = combined;

    setNodes((prev) => {
      const prevById = new Map(prev.map((n) => [n.id, n]));
      return derivedNodes.map((fresh) => {
        const kept = prevById.get(fresh.id);
        if (kept) {
          // Keep user-dragged position, but refresh data.
          return { ...fresh, position: kept.position };
        }
        return fresh;
      });
    });
    setEdges(derivedEdges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, descriptionsFingerprint, snapshotFingerprint]);

  // Canvas viewport behavior:
  //   • On FIRST render (transition 0 → N nodes), fit the view once so the
  //     user sees the whole graph. `maxZoom: 1.0` prevents aggressive
  //     zoom-in when there's only one node.
  //   • On every subsequent add, DO NOTHING — no auto-pan, no auto-zoom.
  //     Earlier behavior soft-panned to the newest node which the user
  //     found annoying ("auto zooming onto the latest canvas"). The user
  //     can scroll / pan / zoom manually instead.
  const { fitView, setCenter } = useReactFlow();

  // Compute the (x, y) center coordinates of a given cell by its index
  // within `alertCells`. Mirrors the grid math in `derivedNodes` (zig-zag
  // two-column layout, plus the hero right-column when the last cell has a
  // dashboard output on the /alerts route). Used by:
  //   • mobile-landing focus (jumps to the LAST cell — the dashboard hero —
  //     so the user sees the alert's answer first instead of a half-fit
  //     zig-zag that makes them pinch-zoom to find what matters)
  //   • the mobile prev / next / output tour pill (same math as above,
  //     keeps scroll locations identical to what the user sees statically)
  //
  // Why not `getNodes()` like StrategyCanvas? React Flow can return the
  // nodes pre-layout during first render on mobile. Using the same math
  // that populates derivedNodes guarantees coords exist even on frame 0.
  const cellCenterAt = useCallback(
    (index: number): { x: number; y: number } | null => {
      const n = alertCells.length;
      if (index < 0 || index >= n) return null;
      const cell = alertCells[index];
      const outputs = Array.isArray(cell?.outputs) ? cell!.outputs : [];
      const isHero =
        isAlertsRoute &&
        index === n - 1 &&
        outputs.some((o) => isDashboardOutput(o));

      // Mobile AND desktop use the SAME two-column zig-zag layout. Earlier
      // attempt at a single-column mobile stack was rejected by the user —
      // they want to see the flow shape on mobile too, just zoomed out so
      // all nodes fit. Initial mobile landing now uses fitView (see the
      // landedOnLastRef effect below) to zoom out enough that every node
      // is visible; user pinches to zoom in.
      if (isHero) {
        const nonHeroCount = n - 1;
        const nonHeroLastRow =
          nonHeroCount > 0 ? Math.floor((nonHeroCount - 1) / 2) : 0;
        const zigzagHeight = nonHeroLastRow * ROW_STEP + NODE_ROW_HEIGHT;
        const x = HERO_COL_X + HERO_NODE_WIDTH / 2;
        const y =
          Math.max(0, (zigzagHeight - HERO_NODE_HEIGHT) / 2) +
          HERO_NODE_HEIGHT / 2;
        return { x, y };
      }
      const col = index % 2;
      const row = Math.floor(index / 2);
      const x = (col === 0 ? COL_X_LEFT : COL_X_RIGHT) + NODE_WIDTH / 2;
      const y = row * ROW_STEP + NODE_ROW_HEIGHT / 2;
      return { x, y };
    },
    [alertCells, isAlertsRoute],
  );

  // Tour state for the mobile navigation pill. `currentTourIndex` tracks
  // which alert step the user is viewing. Default is the LAST index (the
  // dashboard hero / final output) because on a ~400px viewport the user
  // landing on step 1 of 5 is useless — they want the answer first, then
  // can walk back through reasoning with prev/next.
  const [currentTourIndex, setCurrentTourIndex] = useState(0);

  const jumpToTourIndex = useCallback(
    (index: number) => {
      const coords = cellCenterAt(index);
      if (!coords) return;
      // Mobile: zoom 1.0 so the chosen node roughly fills the small
      // viewport. Desktop callers (if any) can override separately.
      setCenter(coords.x, coords.y, { zoom: 1.0, duration: 450 });
      setCurrentTourIndex(index);
    },
    [cellCenterAt, setCenter],
  );

  const goToPrevTour = useCallback(() => {
    if (currentTourIndex > 0) jumpToTourIndex(currentTourIndex - 1);
  }, [currentTourIndex, jumpToTourIndex]);

  const goToNextTour = useCallback(() => {
    if (currentTourIndex < alertCells.length - 1) {
      jumpToTourIndex(currentTourIndex + 1);
    }
  }, [currentTourIndex, alertCells.length, jumpToTourIndex]);

  const jumpToOutputTour = useCallback(() => {
    if (alertCells.length > 0) jumpToTourIndex(alertCells.length - 1);
  }, [alertCells.length, jumpToTourIndex]);

  // Mobile-only initial view: fit ALL nodes in the viewport so the user
  // sees the complete zig-zag flow shape on landing. Earlier versions
  // used `setCenter(lastNode, zoom:1.0)` which zoomed into a single node
  // and made the rest of the canvas look empty — user feedback: "zoom is
  // still off, it takes me to random places". fitView with generous
  // padding zooms out to show every node; user pinches to zoom in.
  //
  // `minZoom: 0.1` allows React Flow to zoom out far enough that even
  // the desktop two-column zig-zag + wide hero column fits a ~400–500px
  // phone viewport. Fires once per session transition from 0 → N cells.
  const landedOnLastRef = useRef(false);
  useEffect(() => {
    if (!isMobile) return;
    if (landedOnLastRef.current) return;
    if (alertCells.length === 0) return;
    if (!viewportReady) return;
    landedOnLastRef.current = true;
    fitView({
      padding: isPublicSignalTour ? 0.28 : 0.12,
      duration: 0,
      includeHiddenNodes: false,
      maxZoom: isPublicSignalTour ? 0.6 : 1.0,
      minZoom: isPublicSignalTour ? 0.05 : 0.1,
    });
    setCurrentTourIndex(alertCells.length - 1);
  }, [isMobile, alertCells.length, viewportReady, fitView, isPublicSignalTour]);

  const prevCountRef = useRef(0);
  useEffect(() => {
    const n = alertCells.length;
    const prev = prevCountRef.current;
    if (n === prev) return;

    if (prev === 0 && n > 0) {
      // First arrival — fit the whole graph once on DESKTOP. On mobile
      // the dedicated "land on last node" effect above centers on the
      // dashboard hero instead, since fitView-all at ~400px viewport
      // produces a tiny unreadable zoom. Skip here so we don't fight it.
      if (isMobile) {
        prevCountRef.current = n;
        return;
      }
      const t = setTimeout(() => {
        fitView({
          padding: 0.3,
          duration: 500,
          includeHiddenNodes: false,
          maxZoom: 1.0,
          minZoom: 0.5,
        });
      }, 220);
      prevCountRef.current = n;
      return () => clearTimeout(t);
    }

    // Subsequent additions: do NOT auto-pan or auto-zoom. The user
    // explicitly asked for this behavior to be off — auto-zooming onto
    // the newest node interrupts their scroll/pan and is disorienting.
    // Just record the new count and let the user navigate the canvas
    // manually.
    prevCountRef.current = n;
  }, [alertCells.length, fitView, isMobile]);

  // Sticky "has ever shown nodes" flag. Once we've rendered at least one
  // alert cell, we do NOT flip back to the empty state on subsequent polls
  // that transiently return zero cells. This eliminates the AnimatePresence
  // flow↔empty swap that looked like a full canvas reload when the user
  // sent their first message (the streaming refetch occasionally races
  // ahead of the first cell's status update).
  const [hasEverShownNodes, setHasEverShownNodes] = useState(false);
  useEffect(() => {
    if (alertCells.length > 0 && !hasEverShownNodes) {
      setHasEverShownNodes(true);
    }
  }, [alertCells.length, hasEverShownNodes]);
  // Reset the sticky flag if the user navigates to a different session.
  useEffect(() => {
    setHasEverShownNodes(false);
  }, [sessionId]);

  const empty = !cellsLoading && alertCells.length === 0 && !hasEverShownNodes;

  // ── Re-fit viewport when the canvas container resizes ─────────────────
  // On the /alerts/[id] page the chat column slides in/out and the canvas
  // column goes from full-width (landing) to ~60% (split view) and back.
  // React Flow's internal nodes keep their world-coords but the viewport
  // transform doesn't auto-center, so nodes appear shifted to the right
  // when the canvas shrinks. This ResizeObserver catches size changes
  // >24px wide, waits for the 400ms slide animation to finish, then calls
  // fitView to re-center. Debounced so rapid resizes only trigger once.
  const canvasWrapperRef = useRef<HTMLDivElement | null>(null);

  // One-shot DOM walker: dumps the padding/margin/width of every ancestor
  // from the canvas wrapper up to <body>. Used to pinpoint where mystery
  // outer padding comes from. Fires once, 1 second after mount (so layout
  // has settled). Remove once the padding bug is found.
  useEffect(() => {
    const t = setTimeout(() => {
      const el = canvasWrapperRef.current;
      if (!el) return;
      const chain: Array<Record<string, unknown>> = [];
      let node: HTMLElement | null = el;
      let depth = 0;
      while (node && depth < 30) {
        const rect = node.getBoundingClientRect();
        const cs = window.getComputedStyle(node);
        chain.push({
          depth,
          tag: node.tagName.toLowerCase(),
          id: node.id || undefined,
          cls: (node.className || "").toString().slice(0, 140),
          w: Math.round(rect.width),
          h: Math.round(rect.height),
          x: Math.round(rect.left),
          y: Math.round(rect.top),
          pad: `${cs.paddingTop} ${cs.paddingRight} ${cs.paddingBottom} ${cs.paddingLeft}`,
          mar: `${cs.marginTop} ${cs.marginRight} ${cs.marginBottom} ${cs.marginLeft}`,
          bor: `${cs.borderTopWidth} ${cs.borderRightWidth} ${cs.borderBottomWidth} ${cs.borderLeftWidth}`,
        });
        node = node.parentElement;
        depth += 1;
      }
      timingLog("dom:chain", { t: tSinceNav(), chain });
    }, 1000);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    const el = canvasWrapperRef.current;
    if (!el) return;
    if (typeof ResizeObserver === "undefined") return;

    let prevWidth = el.getBoundingClientRect().width;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (Math.abs(w - prevWidth) < 24) continue; // ignore tiny changes
        prevWidth = w;
        if (debounceTimer) clearTimeout(debounceTimer);
        // 420ms gives the 400ms chat-slide animation time to finish so
        // we fit to the final container size, not an in-flight one.
        debounceTimer = setTimeout(() => {
          try {
            const usePublicMobileFit = isMobile && isPublicSignalTour;
            fitView({
              padding: usePublicMobileFit ? 0.28 : 0.3,
              duration: 300,
              maxZoom: usePublicMobileFit ? 0.6 : 1.0,
              minZoom: usePublicMobileFit ? 0.05 : 0.5,
            });
          } catch (e) {
            // fitView can throw if the ReactFlow is unmounted. Safe to
            // swallow — next resize will retry.
          }
        }, 420);
      }
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [fitView, isMobile, isPublicSignalTour]);

  return (
    <div
      ref={canvasWrapperRef}
      className="relative w-full h-full"
      // Stable hook for the onboarding-buddy tour. Lets a buddy beat
      // anchor a spotlight to the entire canvas surface (e.g. the
      // "drag to pan" gesture demo and the canvas-intro beat). Kept
      // here on the OUTERMOST wrapper so the spotlight covers the
      // empty-state placeholder AND the React Flow surface alike —
      // anchoring deeper would lose the empty-state intro frame.
      data-testid="alert-canvas"
    >
      {/* Darker icy base — user wanted the canvas darker, not light. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(3,7,18,0.98) 0%, rgba(2,6,15,0.98) 100%)",
        }}
      />
      {/* NOTE: the CSS dot-grid layer was removed here — React Flow's own
           <Background variant="Dots" /> renders the dots below, so stacking
           two grids produced the "doubled dots" the user flagged. Keeping
           only the RF layer gives us one clean, pannable grid.             */}
      {/* Subtle icy glow vignette — depth without washing out the darker base. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            viewMode === "signal"
              ? "radial-gradient(ellipse at 50% 10%, rgba(52, 211, 153, 0.10) 0%, transparent 60%)"
              : "radial-gradient(ellipse at 50% 10%, rgba(125, 211, 252, 0.08) 0%, transparent 60%)",
        }}
      />

      <AnimatePresence mode="wait">
        {empty ? (
          <motion.div
            key="empty"
            className="absolute inset-0 z-10 flex items-center justify-center"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <div className="relative flex flex-col items-center gap-[21px] text-center max-w-[320px] px-6">
              <motion.div
                className={`relative w-14 h-14 rounded-full flex items-center justify-center ${viewMode === "signal" ? "bg-emerald-300/10 ring-1 ring-emerald-300/25" : "bg-sky-300/10 ring-1 ring-sky-300/25"}`}
                animate={{
                  boxShadow: [
                    viewMode === "signal" ? "0 0 0 0 rgba(110,231,183,0.35)" : "0 0 0 0 rgba(186,230,253,0.35)",
                    viewMode === "signal" ? "0 0 0 14px rgba(110,231,183,0)" : "0 0 0 14px rgba(186,230,253,0)",
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
              >
                <Bell className={`w-6 h-6 ${viewMode === "signal" ? "text-emerald-200" : "text-sky-200"}`} strokeWidth={1.8} />
              </motion.div>
              <div className="flex flex-col gap-4 items-center">
                <div className={`flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.14em] uppercase justify-center ${viewMode === "signal" ? "text-emerald-200" : "text-sky-200"}`}>
                  <Sparkles className={`w-3 h-3 ${viewMode === "signal" ? "text-emerald-300" : "text-sky-300"}`} />
                  {viewMode === "signal" ? "Signal Canvas" : "Alert Canvas"}
                </div>
                <p className={"text-slate-50 text-base font-semibold leading-snug"}>
                  Waiting for the agent to lay out your alert
                </p>
                <p className="text-[12.5px] text-slate-100/85 leading-relaxed">
                  Every step will show here as a node once the agent starts
                  building.
                </p>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="flow"
            className="absolute inset-0 z-10"
            // Gate the WHOLE flow column opacity on viewportReady — not just
            // on mount. Previously this faded in the moment ReactFlow mounted,
            // which meant the user saw an empty dotted grid for ~1.3s while
            // fitView was calculating, then the fitView physically translated
            // the ReactFlow viewport to center the nodes (the "snap right to
            // left" the user reported). Now the column stays at opacity:0
            // until onInit → 2× rAF → viewportReady=true, then fades in with
            // the nodes already at their final position.
            initial={{ opacity: 0 }}
            animate={{ opacity: viewportReady ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* Hides nodes UNTIL fitView has locked the viewport so we don't
                see the half-drawn "nodes off to the left" flash. Once ready,
                the wrapper class flips and nodes fade-in with a CSS stagger
                keyed off --idx (set per-node via position.data).  */}
            <div
              className={[
                "absolute inset-0",
                viewportReady ? "alert-canvas-ready" : "alert-canvas-mounting",
              ].join(" ")}
            >
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView
                fitViewOptions={{
                  padding: 0.3,
                  maxZoom: 1.0,
                  minZoom: 0.5,
                }}
                onInit={(instance) => {
                  timingLog("canvas:reactflow:onInit", { t: tSinceNav() });
                  // Belt-and-suspenders: fit once more after mount so edges
                  // have been measured, then reveal the flow.
                  requestAnimationFrame(() => {
                    instance.fitView({
                      padding: 0.3,
                      maxZoom: 1.0,
                      minZoom: 0.5,
                      duration: 0,
                    });
                    requestAnimationFrame(() => {
                      timingLog("canvas:viewportReady", { t: tSinceNav() });
                      telemetry.emit("artifact.canvas_ready", {
                        session_id: sessionId,
                        alert_id: alertId,
                        t: tSinceNav(),
                        node_count: nodes.length,
                        derived_node_count: derivedNodes.length,
                        filtered_alert_cells: alertCells.length,
                        is_mobile: isMobile,
                      });
                      setViewportReady(true);
                    });
                  });
                }}
                proOptions={{ hideAttribution: true }}
                panOnScroll
                zoomOnScroll={false}
                nodesDraggable
                nodesConnectable={false}
                edgesFocusable={false}
                selectNodesOnDrag={false}
                minZoom={0.35}
                maxZoom={1.4}
              >
                <Background
                  variant={BackgroundVariant.Dots}
                  gap={24}
                  size={1.8}
                  color={viewMode === "signal" ? "rgba(110, 231, 183, 0.42)" : "rgba(186, 230, 253, 0.48)"}
                />
              </ReactFlow>
            </div>

            {/* Badge — anchors top-right on DESKTOP, swaps to top-LEFT on
                MOBILE so the vertical nav pill (also top-right on mobile)
                doesn't collide. Styling differs by route:
                  - /alerts/[id]  → emerald "N steps live" — alert IS running,
                                    APScheduler is firing it on cron, every
                                    cell is part of a scheduled task.
                  - /chat (alert) → amber "N steps pending" — cells are
                                    DRAFT. User hasn't confirmed the test
                                    email yet, agent hasn't called
                                    upload_alert, nothing is scheduled.
                                    "Live" would be misleading. */}
            {isAlertsRoute ? (
              <div className="absolute top-3 left-3 md:left-auto md:right-3 z-20 inline-flex items-center gap-1.5 rounded-full border border-emerald-300/35 bg-slate-950/85 backdrop-blur-sm px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-100 shadow-lg">
                <span className="relative inline-flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-80 animate-ping" />
                  <span className="relative rounded-full bg-emerald-400 w-1.5 h-1.5" />
                </span>
                <Inbox className="w-3 h-3" />
                <span>
                  {alertCells.length} step{alertCells.length === 1 ? "" : "s"} live
                </span>
              </div>
            ) : (
              <div className="absolute top-3 left-3 md:left-auto md:right-3 z-20 inline-flex items-center gap-1.5 rounded-full border border-amber-300/35 bg-slate-950/85 backdrop-blur-sm px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-100 shadow-lg">
                <span className="relative inline-flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-amber-400 opacity-70 animate-ping" />
                  <span className="relative rounded-full bg-amber-400 w-1.5 h-1.5" />
                </span>
                <Inbox className="w-3 h-3" />
                <span>
                  {alertCells.length} step{alertCells.length === 1 ? "" : "s"} pending
                </span>
              </div>
            )}

            {/* MOBILE-ONLY: vertical navigation pill — prev / step position /
                next / divider / "jump to output" shortcut. Mirrors the same
                pattern used on /curated/[strategy] (StrategyCanvas.tsx) so
                both tour surfaces feel consistent. On mobile the user lands
                on the LAST node (the dashboard hero) so they see the alert's
                answer first; prev/next walks them back through the reasoning
                without having to pinch-zoom a full zig-zag graph on a 4"
                viewport. Hidden on desktop where pan/zoom is already fine. */}
            {isMobile && alertCells.length > 0 && (
              <div data-tour="alert-mobile-step-nav" className="absolute top-4 right-4 z-50 md:hidden">
                <div
                  className="flex flex-col items-center gap-0.5 px-1 py-1.5 rounded-2xl border border-border/60 shadow-2xl"
                  style={{
                    background: "rgba(9, 9, 11, 0.85)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                  }}
                >
                  <button
                    type="button"
                    onClick={goToPrevTour}
                    disabled={currentTourIndex === 0}
                    aria-label="Previous step"
                    className="flex items-center justify-center w-7 h-7 rounded-full text-foreground hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>

                  <div className="py-1 text-[10px] font-medium text-muted-foreground text-center tabular-nums">
                    <span className="text-foreground">
                      {currentTourIndex + 1}
                    </span>
                    <span className="text-[8px] mx-0.5">/</span>
                    <span className="text-[9px]">{alertCells.length}</span>
                  </div>

                  <button
                    type="button"
                    onClick={goToNextTour}
                    disabled={currentTourIndex >= alertCells.length - 1}
                    aria-label="Next step"
                    className="flex items-center justify-center w-7 h-7 rounded-full text-foreground hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>

                  <div className="h-px w-5 bg-border/60 my-0.5" />

                  <button
                    type="button"
                    onClick={jumpToOutputTour}
                    aria-label="Jump to output"
                    title="Jump to output"
                    className="flex flex-col items-center justify-center w-7 h-7 rounded-full bg-foreground text-background hover:bg-foreground/90 transition-colors"
                  >
                    <LayoutDashboard className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Staggered fade-in, opacity-ONLY.
             *
             * IMPORTANT: React Flow applies its own `transform: translate(x, y)`
             * to every `.react-flow__node` to position it in the flow plane.
             * Animating `transform` here (e.g. translateY/scale in the keyframes)
             * overrides that positional transform — nodes collapse to (0, 0),
             * overlap each other, and DRAG BREAKS because React Flow can no
             * longer read the element's computed transform.
             *
             * So we animate `opacity` only. Stagger per-node via `--alert-node-idx`
             * (set on each node's `style` in the node factory above). Edges fade
             * in slightly after their source node.
             */}
            <style jsx global>{`
              .alert-canvas-mounting .react-flow__node,
              .alert-canvas-mounting .react-flow__edge {
                opacity: 0 !important;
              }
              .alert-canvas-ready .react-flow__node {
                animation: alert-node-in 0.42s ease-out both;
                animation-delay: calc(var(--alert-node-idx, 0) * 55ms + 30ms);
              }
              .alert-canvas-ready .react-flow__edge {
                animation: alert-edge-in 0.45s ease-out both;
                animation-delay: calc(var(--alert-node-idx, 0) * 55ms + 120ms);
              }
              @keyframes alert-node-in {
                from { opacity: 0; }
                to   { opacity: 1; }
              }
              @keyframes alert-edge-in {
                from { opacity: 0; }
                to   { opacity: 1; }
              }

              /* Hover water-wave rings used inside AlertCellNode's hover
                 overlay. Scales a transparent circle outward from the
                 center while fading to 0. A staggered trio (0s / 0.6s /
                 1.2s delays) creates the continuous ripple.            */
              .alert-hover-wave {
                position: absolute;
                left: 50%;
                top: 50%;
                width: 40px;
                height: 40px;
                margin-left: -20px;
                margin-top: -20px;
                border-radius: 9999px;
                border: 1.5px solid rgba(186, 230, 253, 0.55);
                box-shadow: 0 0 18px 0 rgba(186, 230, 253, 0.25) inset,
                  0 0 12px 0 rgba(186, 230, 253, 0.18);
                opacity: 0;
                transform: scale(0);
                animation: alert-hover-wave 1.8s cubic-bezier(0.22, 1, 0.36, 1) infinite;
                pointer-events: none;
              }
              @keyframes alert-hover-wave {
                0% {
                  opacity: 0.55;
                  transform: scale(0);
                }
                80% {
                  opacity: 0;
                  transform: scale(9);
                }
                100% {
                  opacity: 0;
                  transform: scale(9);
                }
              }
              @media (prefers-reduced-motion: reduce) {
                .alert-hover-wave {
                  animation: none;
                  opacity: 0;
                }
              }
            `}</style>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Safe area for Tawk.to Live Support bubble */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[96px] z-0" />
    </div>
  );
}

export interface AlertCanvasProps {
  sessionId: string;
  alertStartCellIndex: number | null;
  isStreaming?: boolean;
  viewMode?: string;
  /** Optional custom header that replaces the internal
   *  "Alert Canvas · ready/building" strip. Used by the /alerts/{id} route
   *  to render the full <AlertTopBar> (with Run now, Pause, Share, Delete,
   *  etc.) above the canvas instead of the generic label. Left undefined
   *  on /chat/{id} so switch_mode("alert") keeps the original strip. */
  customHeader?: React.ReactNode;
  /** True ONLY when mounted under the /alerts/[id] dedicated page. Enables
   *  icon-tab round-robin (3s staggered), the hero right-column last node,
   *  and the water-ripple hover. On /chat (even in alert mode) this stays
   *  false and nodes render static with their single best view. */
  isAlertsRoute?: boolean;
  /** The alert's _id. When set, the canvas fetches the correct snapshot
   *  (LIVE for owner/subscriber, DELAYED-first for everyone else) and
   *  renders it in the last cell as the "Final Results" node. */
  alertId?: string;
  /** Owner of the alert — always sees LIVE dashboard. */
  isOwner?: boolean;
  /** Active subscriber — sees LIVE dashboard. Non-owners who aren't
   *  subscribed see the FIRST snapshot (stale DELAYED teaser). */
  isSubscribed?: boolean;
  /** Per-cell markdown descriptions, keyed by FORK cell index as string
   *  ("1", "2", …). Comes from `alertDoc.cell_descriptions` which the
   *  backend pre-generates at upload time (with a lazy backfill for
   *  legacy alerts). When present, the node's Summary tab renders this
   *  markdown instead of the short cell title. */
  cellDescriptions?: Record<string, string> | null;
  /** Public byline returned from `GET /alerts/{id}`. When `kind === "creator"`,
   *  a floating "Made by {creator}" node is injected above the alert's
   *  zig-zag cell grid. Anonymous fallback or null → no node added. */
  presentedBy?: PublicProfile | null;
}

export function AlertCanvas({
  sessionId,
  alertStartCellIndex,
  isStreaming = false,
  viewMode,
  customHeader,
  isAlertsRoute = false,
  cellDescriptions = null,
  alertId,
  isOwner = false,
  isSubscribed = false,
  presentedBy = null,
}: AlertCanvasProps) {
  const hasCustomHeader = Boolean(customHeader);
  // On the dedicated /alerts/[id] page we render full-bleed — no outer
  // border, no rounded corners, no bottom gap. The canvas owns the
  // entire right column edge-to-edge. On /chat (in alert mode via
  // switch_mode) we keep the framed card look so the canvas clearly
  // reads as an artifact panel next to the chat column.
  const wrapperClass = isAlertsRoute
    ? "h-full relative overflow-hidden bg-background"
    : viewMode === "signal"
      ? "h-[calc(100%-0.5rem)] relative overflow-hidden rounded-lg border border-emerald-400/20 bg-background"
      : "h-[calc(100%-0.5rem)] relative overflow-hidden rounded-lg border border-slate-400/20 bg-background";
  return (
    <AlertCellModalProvider>
      <div className={wrapperClass}>
        {hasCustomHeader ? (
          <div className="relative z-20 shrink-0">{customHeader}</div>
        ) : isAlertsRoute ? null : (
          <div className="relative z-20 flex items-center gap-2 px-4 py-2.5 border-b border-slate-400/15 bg-background/80 backdrop-blur-sm">
            <div className={`relative w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${viewMode === "signal" ? "bg-emerald-300/10 border border-emerald-300/25" : "bg-sky-300/10 border border-sky-300/25"}`}>
              <Bell className={`w-3.5 h-3.5 ${viewMode === "signal" ? "text-emerald-200" : "text-sky-200"}`} strokeWidth={2} />
              <motion.span
                className="absolute inset-0 rounded-md"
                animate={{
                  boxShadow: [
                    viewMode === "signal" ? "0 0 0 0 rgba(110,231,183,0.30)" : "0 0 0 0 rgba(186,230,253,0.30)",
                    viewMode === "signal" ? "0 0 0 6px rgba(110,231,183,0)" : "0 0 0 6px rgba(186,230,253,0)",
                  ],
                }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
              />
            </div>
            <span className={cnTitle()}>{viewMode === "signal" ? "Signal Canvas" : "Alert Canvas"}</span>
            <span className="text-[10.5px] text-slate-400 ml-0.5 uppercase tracking-wider flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" />
              {isStreaming ? "live · building" : "ready"}
            </span>
          </div>
        )}

        <div
          className={
            hasCustomHeader
              ? "relative z-0 h-[calc(100%-56px)] w-full"
              : isAlertsRoute
                ? "relative z-0 h-full w-full"
                : "relative z-0 h-[calc(100%-45px)] w-full"
          }
        >
          <ReactFlowProvider>
            <AlertCanvasInner
              sessionId={sessionId}
              alertStartCellIndex={alertStartCellIndex}
              isStreaming={isStreaming}
              isAlertsRoute={isAlertsRoute}
              cellDescriptions={cellDescriptions}
              alertId={alertId}
              isOwner={isOwner}
              isSubscribed={isSubscribed}
              presentedBy={presentedBy}
              viewMode={viewMode}
            />
          </ReactFlowProvider>
        </div>
      </div>

      <AlertCellModal sessionId={sessionId} viewMode={viewMode} />
    </AlertCellModalProvider>
  );
}

function cnTitle() {
  return `${TITLE_CLASS} text-[12px] tracking-tight`;
}
